import React, { useEffect, useRef, useState } from 'react';
import { 
  FaAws, FaDocker, FaPython, FaLinux, FaGitAlt, FaServer, FaCloud, 
  FaDatabase, FaShieldAlt, FaCode, FaNetworkWired, FaTerminal,
  FaChartLine, FaSync, FaTools, FaRocket, FaMagic, FaCogs,
  FaSun, FaMoon
} from 'react-icons/fa';
import { 
  SiKubernetes, SiTerraform, SiAnsible, SiJenkins, SiAmazon, SiMysql,
  SiPostgresql, SiRedis, SiNginx, SiPrometheus, SiGrafana, SiHelm,
  SiGo, SiReact, SiNodedotjs, SiApache, SiElasticsearch, SiGitlab,
  SiGithubactions, SiApachekafka, SiRabbitmq, SiVagrant, SiPacker
} from 'react-icons/si';
import { TypeAnimation } from 'react-type-animation';

const Skills = () => {
  const [activeCategory, setActiveCategory] = useState('Cloud & DevOps');
  const [hoveredSkill, setHoveredSkill] = useState(null);
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' || 
        (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            entry.target.classList.add('animate-slide-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  const skillCategories = [
    {
      title: 'Cloud & DevOps',
      icon: <FaCloud className="h-6 w-6" />,
      color: 'from-blue-600 to-cyan-500',
      skills: [
        { icon: <FaAws className="h-8 w-8" />, name: 'AWS', level: 'Expert', color: 'text-orange-500 dark:text-orange-400', gradient: 'from-orange-500 to-yellow-500', darkGradient: 'from-orange-400 to-yellow-400', description: 'EC2, S3, RDS, Lambda, IAM, VPC' },
        { icon: <SiKubernetes className="h-8 w-8" />, name: 'Kubernetes', level: 'Expert', color: 'text-blue-600 dark:text-blue-400', gradient: 'from-blue-500 to-cyan-500', darkGradient: 'from-blue-400 to-cyan-400', description: 'Cluster management, Helm, Operators' },
        { icon: <FaDocker className="h-8 w-8" />, name: 'Docker', level: 'Expert', color: 'text-blue-500 dark:text-blue-400', gradient: 'from-blue-400 to-blue-600', darkGradient: 'from-blue-300 to-blue-500', description: 'Containerization, Docker Compose' },
        { icon: <SiTerraform className="h-8 w-8" />, name: 'Terraform', level: 'Expert', color: 'text-purple-600 dark:text-purple-400', gradient: 'from-purple-500 to-pink-500', darkGradient: 'from-purple-400 to-pink-400', description: 'Infrastructure as Code, Modules' },
        { icon: <SiHelm className="h-8 w-8" />, name: 'Helm', level: 'Advanced', color: 'text-blue-700 dark:text-blue-300', gradient: 'from-blue-600 to-indigo-500', darkGradient: 'from-blue-500 to-indigo-400', description: 'Charts, Templating, Releases' },
        { icon: <SiVagrant className="h-8 w-8" />, name: 'Vagrant', level: 'Intermediate', color: 'text-blue-800 dark:text-blue-300', gradient: 'from-blue-700 to-gray-500', darkGradient: 'from-blue-600 to-gray-400', description: 'Development environments' },
      ]
    },
    {
      title: 'CI/CD & Automation',
      icon: <FaSync className="h-6 w-6" />,
      color: 'from-green-600 to-emerald-500',
      skills: [
        { icon: <SiJenkins className="h-8 w-8" />, name: 'Jenkins', level: 'Expert', color: 'text-red-500 dark:text-red-400', gradient: 'from-red-500 to-pink-500', darkGradient: 'from-red-400 to-pink-400', description: 'Pipeline as Code, Shared Libraries' },
        { icon: <SiGithubactions className="h-8 w-8" />, name: 'GitHub Actions', level: 'Advanced', color: 'text-gray-800 dark:text-gray-300', gradient: 'from-gray-700 to-gray-900', darkGradient: 'from-gray-600 to-gray-800', description: 'Workflows, Self-hosted runners' },
        { icon: <SiGitlab className="h-8 w-8" />, name: 'GitLab CI', level: 'Advanced', color: 'text-orange-600 dark:text-orange-400', gradient: 'from-orange-600 to-red-500', darkGradient: 'from-orange-500 to-red-400', description: 'GitLab Runners, Auto DevOps' },
        { icon: <SiAnsible className="h-8 w-8" />, name: 'Ansible', level: 'Advanced', color: 'text-gray-800 dark:text-gray-300', gradient: 'from-gray-700 to-gray-900', darkGradient: 'from-gray-600 to-gray-800', description: 'Playbooks, Roles, Galaxy' },
        { icon: <SiPacker className="h-8 w-8" />, name: 'Packer', level: 'Intermediate', color: 'text-blue-600 dark:text-blue-400', gradient: 'from-blue-500 to-purple-500', darkGradient: 'from-blue-400 to-purple-400', description: 'Machine image creation' },
        { icon: <FaTools className="h-8 w-8" />, name: 'Automation', level: 'Expert', color: 'text-green-600 dark:text-green-400', gradient: 'from-green-500 to-emerald-500', darkGradient: 'from-green-400 to-emerald-400', description: 'Scripting, Workflow automation' },
      ]
    },
    {
      title: 'Monitoring & Observability',
      icon: <FaChartLine className="h-6 w-6" />,
      color: 'from-orange-600 to-red-500',
      skills: [
        { icon: <SiPrometheus className="h-8 w-8" />, name: 'Prometheus', level: 'Advanced', color: 'text-red-400 dark:text-red-300', gradient: 'from-red-400 to-orange-500', darkGradient: 'from-red-300 to-orange-400', description: 'Metrics, AlertManager, Exporters' },
        { icon: <SiGrafana className="h-8 w-8" />, name: 'Grafana', level: 'Advanced', color: 'text-orange-500 dark:text-orange-400', gradient: 'from-orange-500 to-purple-500', darkGradient: 'from-orange-400 to-purple-400', description: 'Dashboards, Alerting, Plugins' },
        { icon: <FaChartLine className="h-8 w-8" />, name: 'CloudWatch', level: 'Advanced', color: 'text-orange-600 dark:text-orange-400', gradient: 'from-orange-600 to-yellow-500', darkGradient: 'from-orange-500 to-yellow-400', description: 'Logs, Metrics, Alarms' },
        { icon: <SiElasticsearch className="h-8 w-8" />, name: 'ELK Stack', level: 'Intermediate', color: 'text-yellow-600 dark:text-yellow-400', gradient: 'from-yellow-500 to-green-500', darkGradient: 'from-yellow-400 to-green-400', description: 'Logstash, Kibana, Beats' },
        { icon: <SiApachekafka className="h-8 w-8" />, name: 'Kafka', level: 'Intermediate', color: 'text-black dark:text-gray-300', gradient: 'from-gray-800 to-black', darkGradient: 'from-gray-700 to-gray-900', description: 'Stream processing, Log aggregation' },
        { icon: <FaDatabase className="h-8 w-8" />, name: 'Monitoring', level: 'Expert', color: 'text-blue-600 dark:text-blue-400', gradient: 'from-blue-500 to-cyan-500', darkGradient: 'from-blue-400 to-cyan-400', description: 'Health checks, Performance metrics' },
      ]
    },
    {
      title: 'Programming & Databases',
      icon: <FaCode className="h-6 w-6" />,
      color: 'from-purple-600 to-pink-500',
      skills: [
        { icon: <FaPython className="h-8 w-8" />, name: 'Python', level: 'Expert', color: 'text-yellow-500 dark:text-yellow-400', gradient: 'from-yellow-500 to-blue-500', darkGradient: 'from-yellow-400 to-blue-400', description: 'Scripting, Automation, Boto3' },
        { icon: <SiGo className="h-8 w-8" />, name: 'Go', level: 'Intermediate', color: 'text-cyan-600 dark:text-cyan-400', gradient: 'from-cyan-500 to-blue-500', darkGradient: 'from-cyan-400 to-blue-400', description: 'CLI tools, Backend services' },
        { icon: <FaLinux className="h-8 w-8" />, name: 'Shell Scripting', level: 'Expert', color: 'text-black dark:text-gray-300', gradient: 'from-gray-800 to-yellow-500', darkGradient: 'from-gray-700 to-yellow-400', description: 'Bash, System administration' },
        { icon: <SiPostgresql className="h-8 w-8" />, name: 'PostgreSQL', level: 'Advanced', color: 'text-blue-700 dark:text-blue-300', gradient: 'from-blue-600 to-cyan-500', darkGradient: 'from-blue-500 to-cyan-400', description: 'High availability, Backup' },
        { icon: <SiMysql className="h-8 w-8" />, name: 'MySQL', level: 'Advanced', color: 'text-blue-500 dark:text-blue-400', gradient: 'from-blue-500 to-blue-700', darkGradient: 'from-blue-400 to-blue-600', description: 'Replication, Optimization' },
        { icon: <SiRedis className="h-8 w-8" />, name: 'Redis', level: 'Advanced', color: 'text-red-600 dark:text-red-400', gradient: 'from-red-600 to-orange-500', darkGradient: 'from-red-500 to-orange-400', description: 'Caching, Pub/Sub, Sessions' },
      ]
    },
    {
      title: 'Networking & Security',
      icon: <FaShieldAlt className="h-6 w-6" />,
      color: 'from-red-600 to-pink-500',
      skills: [
        { icon: <FaNetworkWired className="h-8 w-8" />, name: 'Networking', level: 'Advanced', color: 'text-blue-600 dark:text-blue-400', gradient: 'from-blue-600 to-purple-500', darkGradient: 'from-blue-500 to-purple-400', description: 'VPC, Subnets, Routing' },
        { icon: <FaShieldAlt className="h-8 w-8" />, name: 'Security', level: 'Advanced', color: 'text-green-600 dark:text-green-400', gradient: 'from-green-500 to-emerald-500', darkGradient: 'from-green-400 to-emerald-400', description: 'IAM, Security Groups, WAF' },
        { icon: <SiNginx className="h-8 w-8" />, name: 'Nginx', level: 'Advanced', color: 'text-green-600 dark:text-green-400', gradient: 'from-green-500 to-emerald-600', darkGradient: 'from-green-400 to-emerald-500', description: 'Load balancing, Reverse proxy' },
        { icon: <SiApache className="h-8 w-8" />, name: 'Apache', level: 'Intermediate', color: 'text-red-600 dark:text-red-400', gradient: 'from-red-500 to-gray-500', darkGradient: 'from-red-400 to-gray-400', description: 'Web server, Modules' },
        { icon: <SiRabbitmq className="h-8 w-8" />, name: 'RabbitMQ', level: 'Intermediate', color: 'text-orange-600 dark:text-orange-400', gradient: 'from-orange-500 to-yellow-500', darkGradient: 'from-orange-400 to-yellow-400', description: 'Message queue, AMQP' },
        { icon: <FaTerminal className="h-8 w-8" />, name: 'CLI Tools', level: 'Expert', color: 'text-gray-700 dark:text-gray-300', gradient: 'from-gray-600 to-gray-800', darkGradient: 'from-gray-500 to-gray-700', description: 'AWS CLI, kubectl, terraform' },
      ]
    },
    {
      title: 'Version Control & OS',
      icon: <FaGitAlt className="h-6 w-6" />,
      color: 'from-gray-700 to-gray-900',
      skills: [
        { icon: <FaGitAlt className="h-8 w-8" />, name: 'Git', level: 'Expert', color: 'text-orange-600 dark:text-orange-400', gradient: 'from-orange-600 to-red-500', darkGradient: 'from-orange-500 to-red-400', description: 'GitHub, GitLab, Bitbucket' },
        { icon: <FaLinux className="h-8 w-8" />, name: 'Linux', level: 'Expert', color: 'text-black dark:text-gray-300', gradient: 'from-gray-800 to-yellow-500', darkGradient: 'from-gray-700 to-yellow-400', description: 'Ubuntu, CentOS, Shell scripting' },
        { icon: <FaServer className="h-8 w-8" />, name: 'System Admin', level: 'Advanced', color: 'text-blue-600 dark:text-blue-400', gradient: 'from-blue-500 to-cyan-500', darkGradient: 'from-blue-400 to-cyan-400', description: 'User management, Services' },
        { icon: <FaDatabase className="h-8 w-8" />, name: 'Backup', level: 'Advanced', color: 'text-purple-600 dark:text-purple-400', gradient: 'from-purple-500 to-pink-500', darkGradient: 'from-purple-400 to-pink-400', description: 'Disaster recovery, Snapshots' },
        { icon: <FaMagic className="h-8 w-8" />, name: 'Troubleshooting', level: 'Expert', color: 'text-green-600 dark:text-green-400', gradient: 'from-green-500 to-emerald-500', darkGradient: 'from-green-400 to-emerald-400', description: 'Debugging, Performance tuning' },
        { icon: <FaCogs className="h-8 w-8" />, name: 'Configuration', level: 'Expert', color: 'text-blue-700 dark:text-blue-300', gradient: 'from-blue-600 to-indigo-500', darkGradient: 'from-blue-500 to-indigo-400', description: 'System configuration, Optimization' },
      ]
    }
  ];

  const activeCategoryData = skillCategories.find(cat => cat.title === activeCategory);

  return (
    <section 
      id="skills" 
      ref={containerRef}
      className={`relative py-20 overflow-hidden transition-all duration-500 ${
        darkMode ? 'bg-gray-900' : 'bg-white'
      }`}
    >
      {/* Theme Toggle Button */}
      <button
        onClick={toggleDarkMode}
        className="fixed top-8 right-8 group z-50"
        aria-label="Toggle theme"
      >
        <div className="relative w-12 h-12">
          <div className={`absolute inset-0 rounded-full ${
            darkMode 
              ? 'bg-gradient-to-r from-gray-800 to-gray-900' 
              : 'bg-gradient-to-r from-white to-gray-50'
          } shadow-lg transition-all duration-500`}></div>
          <div className="relative w-full h-full flex items-center justify-center">
            <FaSun className={`absolute w-6 h-6 text-yellow-500 transition-all duration-500 ${
              darkMode ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'
            }`} />
            <FaMoon className={`absolute w-6 h-6 text-blue-400 transition-all duration-500 ${
              darkMode ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'
            }`} />
          </div>
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600/0 to-cyan-500/0 group-hover:from-blue-600/10 group-hover:to-cyan-500/10 transition-all duration-300"></div>
        </div>
      </button>

      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-1/4 left-0 w-96 h-96 rounded-full blur-3xl ${
          darkMode 
            ? 'bg-gradient-to-r from-blue-500/5 to-cyan-500/5' 
            : 'bg-gradient-to-r from-blue-300/10 to-cyan-300/10'
        }`}></div>
        <div className={`absolute bottom-1/4 right-0 w-96 h-96 rounded-full blur-3xl ${
          darkMode 
            ? 'bg-gradient-to-r from-purple-500/5 to-pink-500/5' 
            : 'bg-gradient-to-r from-purple-300/10 to-pink-300/10'
        }`}></div>
        
        {/* Animated tech pattern */}
        <div className={`absolute inset-0 ${
          darkMode ? 'opacity-10' : 'opacity-5'
        }`}>
          <div className="absolute top-10 left-10 animate-float">
            <FaCloud className={`h-12 w-12 ${
              darkMode ? 'text-blue-400' : 'text-blue-400'
            }`} />
          </div>
          <div className="absolute top-1/2 right-20 animate-float" style={{ animationDelay: '1s' }}>
            <FaServer className={`h-10 w-10 ${
              darkMode ? 'text-gray-500' : 'text-gray-400'
            }`} />
          </div>
          <div className="absolute bottom-20 left-1/4 animate-float" style={{ animationDelay: '2s' }}>
            <FaCode className={`h-8 w-8 ${
              darkMode ? 'text-purple-400' : 'text-purple-400'
            }`} />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="relative inline-block mb-6 animate-fade-in">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full blur-xl opacity-30 animate-pulse"></div>
            <div className="relative w-20 h-20 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-full flex items-center justify-center transform hover:rotate-12 transition-transform duration-500 shadow-xl">
              <FaCode className="h-10 w-10 text-white" />
            </div>
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center animate-bounce">
              <span className="text-white text-sm">⚡</span>
            </div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-4 animate-slide-up">
            <span className={darkMode ? 'text-gray-100' : 'text-gray-900'}>Technical </span>
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent animate-gradient">
              Expertise
            </span>
          </h2>
          
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-sm border text-sm font-medium animate-slide-up ${
            darkMode 
              ? 'bg-gradient-to-r from-blue-900/50 to-cyan-900/50 border-blue-800/50 text-cyan-300' 
              : 'bg-gradient-to-r from-blue-100/80 to-cyan-100/80 border-blue-200/50 text-blue-600'
          }`} style={{ animationDelay: '0.2s' }}>
            <div className="w-2 h-2 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full animate-ping"></div>
            <TypeAnimation
              sequence={[
                'Cloud & DevOps Specialist',
                2000,
                'Infrastructure Expert',
                2000,
                'Automation Wizard',
                2000,
                'Production Support Pro',
                2000,
              ]}
              wrapper="span"
              speed={50}
              repeat={Infinity}
              className="font-semibold"
            />
          </div>
        </div>

        {/* Skills Overview Stats */}
        <div className="mb-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: '25+', label: 'Technologies', icon: '🛠️', color: 'from-blue-500 to-cyan-500' },
              { value: '6', label: 'Skill Categories', icon: '📊', color: 'from-purple-500 to-pink-500' },
              { value: '90%', label: 'Expert Level', icon: '🏆', color: 'from-green-500 to-emerald-500' },
              { value: '100%', label: 'Production Ready', icon: '🚀', color: 'from-orange-500 to-red-500' },
            ].map((stat, index) => (
              <div
                key={index}
                className={`backdrop-blur-sm rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-2 border animate-slide-up ${
                  darkMode 
                    ? 'bg-gray-800/50 border-gray-700' 
                    : 'bg-white/90 border-gray-100'
                }`}
                style={{ 
                  animationDelay: `${index * 100}ms`,
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(30px)'
                }}
              >
                <div className={`text-3xl mb-2 bg-gradient-to-br ${stat.color} bg-clip-text text-transparent`}>
                  {stat.value}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{stat.icon}</span>
                  <div className={`text-sm ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Category Navigation */}
        <div className="mb-8">
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {skillCategories.map((category, index) => (
              <button
                key={index}
                onClick={() => setActiveCategory(category.title)}
                className={`group relative px-5 py-3 rounded-xl font-medium transition-all duration-500 transform hover:scale-105 animate-slide-up ${
                  activeCategory === category.title 
                    ? 'shadow-lg' 
                    : 'shadow-sm'
                }`}
                style={{ 
                  animationDelay: `${index * 50}ms`,
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? (activeCategory === category.title ? 'scale(1.05) translateY(0)' : 'translateY(0)') : 'translateY(30px)'
                }}
              >
                {activeCategory === category.title ? (
                  <>
                    <div className={`absolute inset-0 bg-gradient-to-r ${category.color} rounded-xl`}></div>
                    <span className="relative z-10 flex items-center gap-2 text-white">
                      {category.icon}
                      {category.title}
                    </span>
                  </>
                ) : (
                  <>
                    <div className={`absolute inset-0 rounded-xl transition-colors duration-300 ${
                      darkMode 
                        ? 'bg-gray-800/50 group-hover:bg-gray-800/80' 
                        : 'bg-white/50 group-hover:bg-white/80'
                    }`}></div>
                    <span className={`relative z-10 flex items-center gap-2 ${
                      darkMode 
                        ? 'text-gray-300 group-hover:text-gray-100' 
                        : 'text-gray-700 group-hover:text-gray-900'
                    }`}>
                      {category.icon}
                      {category.title}
                    </span>
                  </>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Active Category Skills */}
        {activeCategoryData && (
          <div 
            className="mb-12"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.5s ease-out'
            }}
          >
            <div className="flex items-center justify-between mb-8">
              <h3 className={`text-2xl md:text-3xl font-bold ${
                darkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                  {activeCategoryData.title}
                </span>
              </h3>
              <div className={`flex items-center gap-2 text-sm ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                </div>
                <span>Skill Levels</span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeCategoryData.skills.map((skill, index) => (
                <div
                  key={index}
                  onMouseEnter={() => setHoveredSkill(skill.name)}
                  onMouseLeave={() => setHoveredSkill(null)}
                  className={`group relative overflow-hidden backdrop-blur-sm rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border animate-slide-up ${
                    darkMode 
                      ? 'bg-gray-800/50 border-gray-700' 
                      : 'bg-white/90 border-gray-100'
                  }`}
                  style={{ 
                    animationDelay: `${index * 100}ms`,
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateY(0)' : 'translateY(30px)'
                  }}
                >
                  {/* Skill Level Indicator */}
                  <div className="absolute top-4 right-4">
                    <div className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      skill.level === 'Expert' ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' :
                      skill.level === 'Advanced' ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white' :
                      'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
                    }`}>
                      {skill.level}
                    </div>
                  </div>
                  
                  {/* Icon */}
                  <div className={`mb-4 inline-flex p-3 rounded-xl bg-gradient-to-br ${
                    darkMode ? skill.darkGradient : skill.gradient
                  } text-white transform group-hover:scale-110 transition-transform duration-500`}>
                    {skill.icon}
                  </div>
                  
                  {/* Skill Info */}
                  <h4 className={`text-xl font-bold mb-2 ${skill.color}`}>{skill.name}</h4>
                  <p className={`text-sm mb-4 ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>{skill.description}</p>
                  
                  {/* Progress Bar */}
                  <div className="mt-4">
                    <div className={`flex justify-between text-xs mb-1 ${
                      darkMode ? 'text-gray-500' : 'text-gray-500'
                    }`}>
                      <span>Proficiency</span>
                      <span>{skill.level}</span>
                    </div>
                    <div className={`h-2 rounded-full overflow-hidden ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-200'
                    }`}>
                      <div 
                        className={`h-full rounded-full transition-all duration-1000 ease-out ${
                          darkMode ? skill.darkGradient : skill.gradient
                        }`}
                        style={{ 
                          width: skill.level === 'Expert' ? '95%' : 
                                 skill.level === 'Advanced' ? '85%' : '75%',
                          transform: isVisible ? 'translateX(0)' : 'translateX(-100%)'
                        }}
                      ></div>
                    </div>
                  </div>
                  
                  {/* Hover Effect */}
                  <div className={`absolute bottom-0 left-0 w-0 h-1 ${
                    darkMode ? skill.darkGradient : skill.gradient
                  } transition-all duration-500 group-hover:w-full`}></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Skills Cloud */}
        <div className={`rounded-2xl p-6 md:p-8 backdrop-blur-sm border animate-slide-up ${
          darkMode 
            ? 'bg-gradient-to-r from-blue-900/20 to-cyan-900/20 border-gray-700/30' 
            : 'bg-gradient-to-r from-blue-50/50 to-cyan-50/50 border-blue-100'
        }`} style={{ animationDelay: '600ms' }}>
          <h3 className={`text-2xl font-bold text-center mb-6 ${
            darkMode ? 'text-gray-100' : 'text-gray-900'
          }`}>Technology Stack</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'AWS', 'Kubernetes', 'Docker', 'Terraform', 'Jenkins',
              'Python', 'Linux', 'Git', 'Ansible', 'Helm',
              'Prometheus', 'Grafana', 'PostgreSQL', 'MySQL', 'Nginx',
              'Go', 'Shell Scripting', 'CI/CD', 'IaC', 'Automation'
            ].map((tech, index) => (
              <div
                key={index}
                className={`px-4 py-2 backdrop-blur-sm rounded-full shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-105 cursor-pointer group ${
                  darkMode 
                    ? 'bg-gray-800/50 border-gray-700' 
                    : 'bg-white/80 border-gray-100'
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <span className={`font-medium group-hover:text-blue-600 dark:group-hover:text-blue-400 ${
                  darkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>{tech}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div className={`mt-8 flex flex-wrap justify-center items-center gap-6 text-sm animate-slide-up ${
          darkMode ? 'text-gray-400' : 'text-gray-600'
        }`} style={{ animationDelay: '700ms' }}>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"></div>
            <span>Expert (90-100%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full"></div>
            <span>Advanced (80-90%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full"></div>
            <span>Intermediate (70-80%)</span>
          </div>
        </div>
      </div>

      {/* Custom CSS Animations */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 1s ease-out forwards;
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-slide-up {
          animation: slide-up 0.6s ease-out forwards;
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        .animate-bounce {
          animation: bounce 2s infinite;
        }
      `}</style>
    </section>
  );
};

export default Skills;